import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { BookUser, Plus, Search, Trash2, X } from "lucide-react";

import { deleteExternalUser, listExternalUsers, listExternalUsersReset } from "../../actions";
import { SortMenu } from "@/components/SortMenu";
import { WFPButton } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../components/ui/input-group";
import { getCountryObjectFromCache } from "../../utils";
import { DEFAULT_SORTING, EXTERNAL_USER_SORT_OPTIONS, List } from "./components/List";
import { ConnectedExternalUserModal } from "./components/Modal";
import { type ExternalUser } from "./types";

interface StateProps {
  isFetching: boolean;
  errorMessage: string;
  externalUsers: ExternalUser[];
}

interface ListExternalUsersState {
  isFetching: boolean;
  errorMessage: string;
  data: {
    results: ExternalUser[];
  };
}

interface RootState {
  listExternalUsers: ListExternalUsersState;
}

interface DispatchProps {
  listExternalUsers: () => void;
  listExternalUsersReset: () => void;
  deleteExternalUser: (id: number | string) => Promise<boolean>;
}

type ExternalUsersProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

// The API also returns `person.full_name`, but formatted "Last, First".
const adaptExternalUsers = (externalUsers: ExternalUser[]): ExternalUser[] =>
  externalUsers.map(externalUser => ({
    ...externalUser,
    fullName: [externalUser.person?.first_name, externalUser.person?.last_name]
      .filter(Boolean)
      .join(" "),
  }));

const filterExternalUsers = (externalUsers: ExternalUser[], searchTerm: string) => {
  const query = searchTerm.trim().toLocaleLowerCase();

  if (!query) {
    return externalUsers;
  }

  return externalUsers.filter(externalUser =>
    [externalUser.fullName, externalUser.email, externalUser.organization].some(value =>
      String(value ?? "")
        .toLocaleLowerCase()
        .includes(query)
    )
  );
};

export const ExternalUsers = ({
  isFetching,
  errorMessage,
  externalUsers,
  listExternalUsers,
  listExternalUsersReset,
  deleteExternalUser,
}: ExternalUsersProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [externalUserToDelete, setExternalUserToDelete] = useState<ExternalUser | null>(null);
  const [externalUserToEdit, setExternalUserToEdit] = useState<ExternalUser | null | undefined>(
    undefined
  );

  useEffect(() => {
    listExternalUsers();
    return () => {
      listExternalUsersReset();
    };
  }, [listExternalUsers, listExternalUsersReset]);

  const visibleExternalUsers = useMemo(
    () => filterExternalUsers(externalUsers, searchTerm),
    [externalUsers, searchTerm]
  );

  const selectedSort = sorting[0];
  const selectedSortColumn = selectedSort?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || EXTERNAL_USER_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const onClickConfirmDeleteExternalUser = () => {
    if (externalUserToDelete) {
      deleteExternalUser(externalUserToDelete.id).then(() => listExternalUsers());
    }
    setExternalUserToDelete(null);
  };

  const onClickCancelDeleteExternalUser = () => setExternalUserToDelete(null);

  const closeExternalUserModal = () => setExternalUserToEdit(undefined);

  const onExternalUserSaved = () => {
    closeExternalUserModal();
    listExternalUsers();
  };

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-blue-600 bg-blue-100">
              <BookUser />
            </span>
            <div className="space-y-1">
              <h1 className="font-semibold text-2xl">
                {t("externalUsers.pageTitleWithCountry", {
                  country: getCountryObjectFromCache()?.name ?? "",
                })}
              </h1>
              <p className="max-w-3xl text-muted-foreground text-sm">
                {t("externalUsers.helperText")}
              </p>
            </div>
          </div>
          <WFPButton type="button" className="shrink-0" onClick={() => setExternalUserToEdit(null)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("externalUsers.newExternalUser")}
          </WFPButton>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-full max-w-xs sm:w-80">
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="external-user-search-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("externalUsers.searchPlaceholder")}
              aria-label={t("externalUsers.searchPlaceholder")}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("externalUsers.clearFilter")}
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
          <SortMenu
            options={EXTERNAL_USER_SORT_OPTIONS}
            sortColumn={selectedSortColumn}
            sortDirection={selectedSortDirection}
            onSortColumnChange={onSortColumnChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        </div>
        <List
          isFetching={isFetching}
          errorMessage={errorMessage}
          externalUsers={visibleExternalUsers}
          onClickView={setExternalUserToEdit}
          onClickDelete={setExternalUserToDelete}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </main>
      <ConnectedExternalUserModal
        externalUser={externalUserToEdit ?? null}
        isOpen={externalUserToEdit !== undefined}
        onClose={closeExternalUserModal}
        onSaved={onExternalUserSaved}
      />
      <Dialog
        open={Boolean(externalUserToDelete)}
        onOpenChange={open => {
          if (!open) {
            onClickCancelDeleteExternalUser();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <Trash2 className="size-5" />
              </div>
              <div className="space-y-2">
                <DialogTitle>{t("externalUsers.deleteExternalUserModalTitle")}</DialogTitle>
                <DialogDescription className="whitespace-pre-line text-foreground">
                  {t("externalUsers.deleteExternalUserModalText", {
                    first_name: externalUserToDelete?.person?.first_name,
                    last_name: externalUserToDelete?.person?.last_name,
                  })}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <WFPButton type="button" variant="outline" onClick={onClickCancelDeleteExternalUser}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton
              type="button"
              variant="destructive"
              onClick={onClickConfirmDeleteExternalUser}
            >
              {t("common.delete")}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  const selectedCountry = getCountryObjectFromCache();
  return {
    isFetching: state.listExternalUsers.isFetching,
    errorMessage: state.listExternalUsers.errorMessage,
    externalUsers: adaptExternalUsers(
      state.listExternalUsers.data.results.filter(
        externalUser => externalUser?.country?.id === selectedCountry.id
      )
    ),
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listExternalUsers: () =>
      dispatchAction(dispatch, listExternalUsers({ page: 1, page_size: 999 })),
    listExternalUsersReset: () => dispatchAction(dispatch, listExternalUsersReset()),
    deleteExternalUser: (id: number | string) =>
      dispatchAction(dispatch, deleteExternalUser(id)) as unknown as Promise<boolean>,
  };
};

export const ConnectedExternalUsers = connect(mapStateToProps, mapDispatchToProps)(ExternalUsers);
